import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {takeEvery, getContext, call, put} from 'typed-redux-saga'
import User from 'src/models/User'
import {DataState} from 'src/types/dataState.type'
import Api from 'src/api/reactpt'
import {shouldLogUserOut} from '../configureStore'

export type UsersState = {
  users: User[]
  dataState: DataState
}
const initialState: UsersState = {
  users: [],
  dataState: {state: 'unloaded'},
}

export const {actions, reducer} = createSlice({
  name: 'users',
  initialState,
  reducers: {
    getAllUsers: (_state: UsersState, _action: PayloadAction<void>) => {},
    saveUser: (_state: UsersState, _action: PayloadAction<{user: User}>) => {},
    setUsers: (state: UsersState, action: PayloadAction<{users: User[]}>) => {
      return {...state, users: action.payload.users}
    },
    setDataState: (state: UsersState, action: PayloadAction<{dataState: DataState}>) => {
      const {dataState} = action.payload
      return {...state, dataState}
    },
    clear: (_state: UsersState) => {
      return {...initialState}
    },
  },
})

export const getAllUsersSaga = function* () {
  const api: Api = yield* getContext<Api>('api')
  yield* put(actions.setDataState({dataState: {state: 'loading'}}))
  try {
    const result = yield* call(api.getAllUsers)
    yield* put(actions.setUsers({users: result}))
    yield* put(actions.setDataState({dataState: {state: 'loaded'}}))
  } catch (error) {
    if (shouldLogUserOut(error)) {
      throw error
    } else {
      yield* put(actions.setDataState({dataState: {state: 'unloaded', error}}))
    }
  }
}

export const saveUserSaga = function* (action: ReturnType<typeof actions.saveUser>) {
  const {payload} = action
  const api: Api = yield* getContext<Api>('api')
  yield* put(actions.setDataState({dataState: {state: 'loading'}}))
  try {
    if (payload.user.id) {
      yield* call(api.updateUser, payload.user)
    } else {
      yield* call(api.createUser, payload.user)
    }
    yield* put(actions.getAllUsers())
  } catch (error) {
    if (shouldLogUserOut(error)) {
      throw error
    } else {
      yield* put(actions.setDataState({dataState: {state: 'unloaded', error}}))
    }
  }
}

export const saga = function* () {
  yield* takeEvery(actions.getAllUsers.type, getAllUsersSaga)
  yield* takeEvery(actions.saveUser.type, saveUserSaga)
}
